import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { enablePushNotifications, disablePushNotifications } from '../hooks/usePushNotifications';
import { applyBranding } from '../context/SettingsContext';
import { useT } from '../i18n';
import BrandMark from './BrandMark';

function Navigation({ userInfo, onLogout, onTokenRefresh, disabledNav = false, theme = 'dark', onToggleTheme }) {
  const location = useLocation();
  const navigate = useNavigate();
  const t = useT();
  const isAdmin = userInfo?.role === 'admin';
  const isCoAdmin = userInfo?.is_co_admin || false;
  const isDemo = userInfo?.isDemo || false;
  const displayName = userInfo?.name || userInfo?.email || '';

  const [showSettings, setShowSettings] = useState(false);
  const [colorPrimary,   setColorPrimary]   = useState('var(--brand-primary)');
  const [colorSecondary, setColorSecondary] = useState('#feb700');
  const [logoBase64, setLogoBase64] = useState('');
  const [teamName, setTeamName] = useState('RENNTEAM');
  const [teamNameInput, setTeamNameInput] = useState('RENNTEAM');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installable, setInstallable] = useState(false);
  const [installDone, setInstallDone] = useState(false);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);

  // Password change state
  const [pwCurrent, setPwCurrent] = useState('');
  const [pwNew, setPwNew] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [pwMessage, setPwMessage] = useState(null); // {type: 'success'|'error', text: ''}
  const [pwLoading, setPwLoading] = useState(false);

  // Driver photo (kept in sync with server + prop updates)
  const [driverPhotoUrl, setDriverPhotoUrl] = useState(userInfo?.photo_url || null);
  useEffect(() => {
    if (userInfo?.photo_url) setDriverPhotoUrl(userInfo.photo_url);
  }, [userInfo?.photo_url]);

  // Push notification state
  const [pushPermission, setPushPermission] = useState('default'); // 'default'|'granted'|'denied'
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [pushMsg, setPushMsg] = useState(null);

  const applySettings = useCallback((s) => {
    const color  = s.color_primary   || s.colorPrimary   || 'var(--brand-primary)';
    const color2 = s.color_secondary || s.colorSecondary || '#feb700';
    const logo   = s.logo_base64     || s.logoBase64     || '';
    const name   = s.team_name       || s.teamName       || 'RENNTEAM';
    setColorPrimary(color);
    setColorSecondary(color2);
    setLogoBase64(logo);
    setTeamName(name);
    setTeamNameInput(name);
    applyBranding(color, color2);
  }, []);

  useEffect(() => {
    // 1. Instant render from localStorage cache (no flash)
    const cached = localStorage.getItem('teamSettings');
    if (cached) {
      try { applySettings(JSON.parse(cached)); } catch {}
    }

    // 2. Fetch from API — authoritative source
    const token = localStorage.getItem('token');
    if (token) {
      // For drivers: load fresh photo_url from server (localStorage may be stale)
      if (!isAdmin) {
        axios.get('/api/auth/driver-account', { headers: { Authorization: `Bearer ${token}` } })
          .then(({ data }) => { if (data.photo_url) setDriverPhotoUrl(data.photo_url); })
          .catch(() => {});
      }

      axios.get('/api/settings', { headers: { Authorization: `Bearer ${token}` } })
        .then(({ data }) => {
          applySettings(data);
          // Update cache
          localStorage.setItem('teamSettings', JSON.stringify({
            colorPrimary:   data.color_primary   || 'var(--brand-primary)',
            colorSecondary: data.color_secondary || '#feb700',
            logoBase64:     data.logo_base64     || '',
            teamName:       data.team_name        || 'RENNTEAM',
          }));
        })
        .catch(() => {/* offline — cache already applied */});
    }

    // Poll unread notification count (admin = team notifications, driver = driver notifications)
    const countUrl = isAdmin
      ? '/api/notifications/unread-count'
      : '/api/notifications/driver/unread-count';
    const fetchCount = () => {
      const t = localStorage.getItem('token');
      if (!t) return;
      axios.get(countUrl, { headers: { Authorization: `Bearer ${t}` } })
        .then(({ data }) => setUnreadCount(data.count || 0))
        .catch(() => {});
    };
    fetchCount();
    const interval = setInterval(fetchCount, 30000);
    window._notifInterval = interval;

    // PWA install prompt
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setInstallable(true);
      if (!sessionStorage.getItem('installBannerDismissed')) {
        setShowInstallBanner(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Detect iOS
    const ios = /iphone|ipad|ipod/i.test(navigator.userAgent);
    setIsIOS(ios);

    // Already installed?
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    if (isStandalone) {
      setInstallDone(true);
    } else if (ios && !sessionStorage.getItem('installBannerDismissed')) {
      // Show iOS banner after short delay so page loads first
      setTimeout(() => setShowInstallBanner(true), 1500);
    }

    // Init push notification state
    if ('Notification' in window) {
      setPushPermission(Notification.permission);
      const stored = localStorage.getItem('pushNotificationsEnabled');
      setPushEnabled(Notification.permission === 'granted' && stored !== 'false');
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      if (window._notifInterval) clearInterval(window._notifInterval);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applySettings]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwMessage(null);
    if (pwNew !== pwConfirm) {
      setPwMessage({ type: 'error', text: t('nav_err_pw_match') });
      return;
    }
    if (pwNew.length < 6) {
      setPwMessage({ type: 'error', text: t('nav_err_pw_short') });
      return;
    }
    setPwLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.put('/api/auth/change-password', { currentPassword: pwCurrent, newPassword: pwNew }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.token && onTokenRefresh) onTokenRefresh(res.data.token);
      setPwMessage({ type: 'success', text: t('nav_pw_success') });
      setPwCurrent(''); setPwNew(''); setPwConfirm('');
    } catch (err) {
      setPwMessage({ type: 'error', text: err.response?.data?.error || t('nav_err_change') });
    } finally {
      setPwLoading(false);
    }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setInstallDone(true);
      setInstallable(false);
      setShowInstallBanner(false);
    }
    setDeferredPrompt(null);
  };

  const dismissBanner = () => {
    setShowInstallBanner(false);
    setShowIOSGuide(false);
    sessionStorage.setItem('installBannerDismissed', '1');
  };

  const applyColor = (color) => {
    setColorPrimary(color);
    applyBranding(color, colorSecondary);
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setLogoBase64(ev.target.result);
    reader.readAsDataURL(file);
  };

  const saveSettings = async () => {
    setSaveError(null);
    const name = teamNameInput.trim() || 'RENNTEAM';
    const token = localStorage.getItem('token');
    try {
      await axios.put('/api/settings', {
        team_name:       name,
        color_primary:   colorPrimary,
        color_secondary: colorSecondary,
        logo_base64:     logoBase64,
      }, { headers: { Authorization: `Bearer ${token}` } });
      // Update state + cache
      setTeamName(name);
      localStorage.setItem('teamSettings', JSON.stringify({ colorPrimary, colorSecondary, logoBase64, teamName: name }));
      closeSettings();
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Fehler beim Speichern');
    }
  };

  const closeSettings = () => {
    setShowSettings(false);
    setTeamNameInput(teamName); // reset unsaved changes
    setPwCurrent(''); setPwNew(''); setPwConfirm(''); setPwMessage(null);
    setSaveError(null);
    setPushMsg(null);
  };

  const handlePushToggle = async () => {
    setPushLoading(true);
    setPushMsg(null);
    const token = localStorage.getItem('token');
    try {
      if (pushEnabled) {
        // Turn off
        await disablePushNotifications(token);
        setPushEnabled(false);
        setPushMsg({ type: 'info', text: 'Push-Benachrichtigungen deaktiviert.' });
      } else {
        // Turn on
        if (Notification.permission === 'denied') {
          throw new Error('DENIED');
        }
        await enablePushNotifications(token);
        setPushPermission('granted');
        setPushEnabled(true);
        setPushMsg({ type: 'success', text: t('nav_push_enabled') });
      }
    } catch (err) {
      if (err.message === 'DENIED') {
        setPushPermission('denied');
        setPushMsg({ type: 'blocked', text: null });
      } else if (err.message === 'DISMISSED') {
        setPushMsg({ type: 'error', text: t('nav_push_denied') });
      } else {
        setPushMsg({ type: 'error', text: err.message });
      }
    } finally {
      setPushLoading(false);
    }
  };

  // In demo mode all links are prefixed with /t/:slug
  const demoBase = isDemo ? `/t/${userInfo?.public_slug || ''}` : '';

  const isActive = (path) => {
    // Root path (dashboard) or demo root — exact match only so sub-pages don't highlight it
    const isRoot = path === '/' || (isDemo && path === demoBase);
    if (isRoot) return location.pathname === path || location.pathname === `${path}/`;
    return location.pathname.startsWith(path);
  };

  // SVG icon components
  const IconHouse = ({ size = 18, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/>
      <polyline points="9 21 9 13 15 13 15 21"/>
    </svg>
  );
  const IconRider = ({ size = 18, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="17" cy="18.5" r="2.5"/>
      <circle cx="7" cy="18.5" r="2.5"/>
      <path d="M7 18.5h10"/>
      <path d="M12 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" fill="currentColor" stroke="none"/>
      <path d="M12 3l-2 5h5l3 4.5"/>
      <path d="M10 8l-3 4 4 2"/>
    </svg>
  );
  const IconCalendar = ({ size = 18, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="3" y="4" width="18" height="18" rx="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
  const IconGrid = ({ size = 18, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <line x1="8" y1="3" x2="8" y2="21"/>
      <line x1="16" y1="3" x2="16" y2="21"/>
      <line x1="3" y1="8" x2="21" y2="8"/>
      <line x1="3" y1="16" x2="21" y2="16"/>
      <rect x="3" y="3" width="18" height="18" rx="1"/>
    </svg>
  );
  const IconEnvelope = ({ size = 18, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <polyline points="2,4 12,13 22,4"/>
    </svg>
  );
  const IconGear = ({ size = 16, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  );
  const IconPower = ({ size = 16, style }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={style}>
      <path d="M18.36 6.64A9 9 0 1 1 5.64 6.64"/>
      <line x1="12" y1="2" x2="12" y2="12"/>
    </svg>
  );

  // Profile icon: photo if available, else initials avatar
  const profileInitials = userInfo?.name ? userInfo.name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) : '?';
  const IconProfile = ({ size = 22 }) =>
    driverPhotoUrl ? (
      <img src={driverPhotoUrl} alt="Profil" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', display: 'block' }} />
    ) : (
      <span style={{ width: size, height: size, borderRadius: '50%', background: 'rgba(var(--primary-rgb),0.2)', border: '1.5px solid var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: size * 0.38, fontWeight: 700, color: 'var(--primary)', flexShrink: 0 }}>
        {profileInitials}
      </span>
    );

  const IconArchive = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
  const IconSun = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/>
      <line x1="12" y1="2" x2="12" y2="5"/>
      <line x1="12" y1="19" x2="12" y2="22"/>
      <line x1="4.22" y1="4.22" x2="6.34" y2="6.34"/>
      <line x1="17.66" y1="17.66" x2="19.78" y2="19.78"/>
      <line x1="2" y1="12" x2="5" y2="12"/>
      <line x1="19" y1="12" x2="22" y2="12"/>
      <line x1="4.22" y1="19.78" x2="6.34" y2="17.66"/>
      <line x1="17.66" y1="6.34" x2="19.78" y2="4.22"/>
    </svg>
  );
  const IconMoon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );

  const navItems = [
    { to: isDemo ? demoBase : '/',                     label: t('nav_dashboard'),     icon: <IconHouse size={26} /> },
    { to: `${demoBase}/drivers`,                       label: t('nav_drivers'),       icon: <IconRider size={26} /> },
    { to: `${demoBase}/events`,                        label: t('nav_events'),        icon: <IconCalendar size={26} /> },
    { to: `${demoBase}/matrix`,                        label: t('nav_matrix'),        icon: <IconGrid size={26} /> },
    { to: `${demoBase}/past-seasons`,                  label: 'Vergangene Saisons',   icon: <IconArchive size={26} /> },
    { to: `${demoBase}/notifications`,                 label: t('nav_notifications'), icon: <IconEnvelope size={26} />, badge: unreadCount },
    ...(isAdmin
      ? [{ to: `${demoBase}/owner-profile`, label: t('nav_profile'), icon: <IconProfile size={28} /> }]
      : [{ to: `${demoBase}/profile`,       label: t('nav_profile'), icon: <IconProfile size={28} /> }]
    ),
  ];

  return (
    <>
      {/* ── DESKTOP SIDEBAR ── */}
      <aside className="app-sidebar">
        <div className="sidebar-brand">
          <div className="sidebar-brand-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {logoBase64
              ? <img src={logoBase64} alt="Logo" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 3 }} />
              : <span style={{ width: 28, height: 28, borderRadius: 3, background: 'var(--accent-tint-strong)', border: '1px solid rgba(0,219,233,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}><BrandMark size={22} variant="cyan" /></span>
            }
            <span style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 800, fontSize: 14, letterSpacing: '0.04em', color: 'var(--text-primary)' }}>{teamName}</span>
          </div>
          <div style={{ marginTop: 4, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-micro)', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--accent)', textTransform: 'uppercase' }}>
            powered by Crit Roster
          </div>
          {displayName && (
            <div style={{ marginTop: 8, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-body)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              <span style={{ padding: '1px 5px', background: isAdmin ? 'var(--accent-tint)' : isCoAdmin ? 'rgba(255,154,31,0.15)' : 'rgba(171,214,0,0.12)', color: isAdmin ? 'var(--accent)' : isCoAdmin ? '#ff9a1f' : 'var(--accent-2)', borderRadius: 2, fontSize: 9 }}>
                {isAdmin ? t('nav_role_owner') : isCoAdmin ? t('nav_role_manager') : t('nav_role_rider')}
              </span>
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {navItems.map(({ to, label, icon, badge }) => (
            disabledNav
              ? <span key={to} className="sidebar-link" style={{ opacity: 0.3, cursor: 'not-allowed', pointerEvents: 'none' }}>
                  <span className="sidebar-link-icon" style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
                  {label}
                </span>
              : <Link key={to} to={to} className={`sidebar-link${isActive(to) ? ' active' : ''}`} style={{ position: 'relative' }}>
                  <span className="sidebar-link-icon" style={{ display: 'flex', alignItems: 'center' }}>{icon}</span>
                  {label}
                  {badge > 0 && (
                    <span style={{ marginLeft: 'auto', minWidth: 18, height: 18, borderRadius: 9, background: 'var(--primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingInline: 4, boxShadow: '0 0 8px rgba(var(--primary-rgb),0.6)' }}>
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </Link>
          ))}
        </nav>

        <div className="sidebar-bottom">
          {isDemo ? (
            /* ── Demo mode: CTA + exit ── */
            <div>
              <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(255,154,31,0.08)', border: '1px solid rgba(255,154,31,0.25)' }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.15em', color: '#ff9a1f', marginBottom: 4 }}>{t('nav_demo_mode')}</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 11, color: 'var(--on-surface-variant)', lineHeight: 1.4 }}>
                  {t('nav_demo_readonly')}
                </div>
              </div>
              <button
                onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('userInfo'); window.location.href = '/register'; }}
                style={{ display: 'block', width: '100%', padding: '10px 14px', background: 'var(--primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', border: 'none', cursor: 'pointer', textAlign: 'center', marginBottom: 8 }}
              >
                {t('nav_demo_start_free')}
              </button>
              <button className="sidebar-bottom-btn" onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('userInfo'); window.location.href = '/'; }} style={{ color: 'var(--outline)' }}>
                <IconPower style={{ flexShrink: 0 }} />
                {t('nav_demo_end')}
              </button>
            </div>
          ) : (
            /* ── Normal mode ── */
            <>
              {!disabledNav && (
                <button className="sidebar-bottom-btn" onClick={() => navigate('/settings')}>
                  <IconGear style={{ flexShrink: 0 }} />
                  {t('nav_settings')}
                </button>
              )}
              {/* ── Theme Toggle ── */}
              {onToggleTheme && (
                <button
                  onClick={onToggleTheme}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '8px 12px', marginBottom: 4,
                    background: 'transparent',
                    border: '1px solid var(--border-default)',
                    borderRadius: 'var(--radius-xs)',
                    cursor: 'pointer',
                    color: 'var(--text-body)',
                  }}
                  title={theme === 'dark' ? 'Light Mode aktivieren' : 'Dark Mode aktivieren'}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {theme === 'dark' ? <IconSun size={15} /> : <IconMoon size={15} />}
                    <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--text-label)', textTransform: 'uppercase' }}>
                      {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
                    </span>
                  </span>
                  {/* Pill toggle */}
                  <span style={{
                    position: 'relative', display: 'inline-block',
                    width: 34, height: 20, flexShrink: 0,
                    borderRadius: 10,
                    background: theme === 'light' ? 'var(--accent)' : 'rgba(59,73,75,0.5)',
                    transition: 'background var(--dur-normal)',
                  }}>
                    <span style={{
                      position: 'absolute', top: 2,
                      left: theme === 'light' ? 16 : 2,
                      width: 16, height: 16, borderRadius: '50%',
                      background: '#fff',
                      transition: 'left var(--dur-normal)',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                    }} />
                  </span>
                </button>
              )}
              {isCoAdmin && !disabledNav && (
                <div style={{ marginBottom: 4, padding: '2px 8px', background: 'rgba(255,154,31,0.1)', border: '1px solid rgba(255,154,31,0.25)', borderRadius: 2, fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, letterSpacing: '0.1em', color: '#ff9a1f', textAlign: 'center' }}>
                  TEAM MANAGER
                </div>
              )}
              <button className="sidebar-bottom-btn" onClick={onLogout}>
                <IconPower style={{ flexShrink: 0 }} />
                {t('nav_logout')}
              </button>
            </>
          )}
        </div>
      </aside>

      {/* ── MOBILE TOPBAR ── */}
      <header className="mobile-topbar">
        <div className="mobile-topbar-brand" style={{ flex: 1, minWidth: 0 }}>
          {logoBase64
            ? <img src={logoBase64} alt="Logo" style={{ width: 28, height: 28, objectFit: 'cover', borderRadius: 2, flexShrink: 0 }} />
            : <span style={{ width: 28, height: 28, borderRadius: 2, background: 'var(--accent-tint-strong)', border: '1px solid rgba(0,219,233,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}><BrandMark size={22} variant="cyan" /></span>
          }
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, minWidth: 0 }}>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 800, fontStyle: 'italic', letterSpacing: '0.04em', color: 'var(--text-primary)', wordBreak: 'break-word' }}>{teamName}</span>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--accent)', opacity: 0.85, marginTop: 2 }}>powered by Crit Roster</span>
          </div>
        </div>
        {isDemo && (
          <button
            onClick={() => { localStorage.removeItem('token'); localStorage.removeItem('userInfo'); window.location.href = '/register'; }}
            style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', background: '#ff9a1f', color: '#1a0a00', padding: '6px 10px', border: 'none', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            {t('nav_demo_mobile_start')}
          </button>
        )}
        {/* Theme toggle icon (mobile) */}
        {onToggleTheme && (
          <button
            className="mobile-icon-btn"
            onClick={onToggleTheme}
            style={{ flexShrink: 0 }}
            title={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            aria-label={theme === 'dark' ? 'Light Mode aktivieren' : 'Dark Mode aktivieren'}
          >
            {theme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
          </button>
        )}
      </header>

      {/* ── MOBILE INSTALL BANNER ── */}
      {showInstallBanner && !installDone && (
        <div style={{
          position: 'fixed',
          top: 'calc(86px + env(safe-area-inset-top))',
          left: 0, right: 0,
          zIndex: 190,
          background: 'rgba(0,30,34,0.97)',
          borderBottom: '1px solid rgba(var(--brand-primary-rgb),0.3)',
          backdropFilter: 'blur(16px)',
          padding: '12px 16px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.4)',
        }}>
          {!showIOSGuide ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 2 }}>{t('nav_install_app')}</div>
                <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'var(--on-surface-variant)', lineHeight: 1.4 }}>
                  {isIOS ? t('nav_install_hint_ios') : t('nav_install_hint_android')}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                {isIOS ? (
                  <button
                    onClick={() => setShowIOSGuide(true)}
                    style={{ padding: '8px 14px', background: 'var(--primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', border: 'none', cursor: 'pointer' }}
                  >
                    WIE?
                  </button>
                ) : (
                  <button
                    onClick={handleInstall}
                    style={{ padding: '8px 14px', background: 'var(--primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', border: 'none', cursor: 'pointer' }}
                  >
                    INSTALLIEREN
                  </button>
                )}
                <button
                  onClick={dismissBanner}
                  style={{ padding: '8px 10px', background: 'transparent', color: 'var(--outline)', fontFamily: "'JetBrains Mono', monospace", fontSize: 14, border: '1px solid rgba(59,73,75,0.4)', cursor: 'pointer', lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)' }}>{t('nav_how_ios')}</div>
                <button onClick={dismissBanner} style={{ background: 'transparent', border: 'none', color: 'var(--outline)', fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 4px' }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { step: '1', icon: '⬆', text: t('nav_ios_step1') },
                  { step: '2', icon: '＋', text: t('nav_ios_step2') },
                  { step: '3', icon: '✓', text: t('nav_ios_step3') },
                ].map(({ step, icon, text }) => (
                  <div key={step} style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{ width: 28, height: 28, background: 'rgba(var(--brand-primary-rgb),0.12)', border: '1px solid rgba(var(--brand-primary-rgb),0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 14, color: 'var(--primary)' }}>{icon}</div>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'var(--on-surface-variant)', lineHeight: 1.4, paddingTop: 4 }}>{text}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── MOBILE BOTTOM NAV ── */}
      <nav className="mobile-bottom-nav">
        {navItems.map(({ to, label, icon, badge }) => (
          disabledNav
            ? <span key={to} className="mobile-nav-item" style={{ opacity: 0.3, cursor: 'not-allowed', pointerEvents: 'none' }}>
                <span className="mobile-nav-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{icon}</span>
              </span>
            : <Link key={to} to={to} className={`mobile-nav-item${isActive(to) ? ' active' : ''}`} style={{ position: 'relative' }} title={label}>
                <span className="mobile-nav-icon" style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {icon}
                  {badge > 0 && (
                    <span style={{ position: 'absolute', top: -4, right: -6, minWidth: 14, height: 14, borderRadius: 7, background: 'var(--primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingInline: 2 }}>
                      {badge > 99 ? '99+' : badge}
                    </span>
                  )}
                </span>
              </Link>
        ))}
        {disabledNav && (
          <button
            onClick={onLogout}
            className="mobile-nav-item"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <span className="mobile-nav-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <IconPower size={20} style={{ color: 'var(--on-surface-variant)' }} />
            </span>
          </button>
        )}
      </nav>

      {/* ── SETTINGS MODAL ── */}
      {showSettings && (
        <div
          onClick={(e) => e.target === e.currentTarget && closeSettings()}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: '1rem' }}
        >
          <div style={{ background: 'var(--surface)', border: '1px solid rgba(59,73,75,0.4)', boxShadow: 'inset 0 0 20px rgba(var(--brand-primary-rgb),0.06)', maxWidth: 420, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1.25rem 1.5rem', borderBottom: '1px solid rgba(59,73,75,0.3)', background: 'rgba(26,28,32,0.5)' }}>
              <div>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--primary)', marginBottom: 4 }}>{t('nav_config')}</div>
                <h2 style={{ color: 'var(--on-surface)', fontStyle: 'italic', margin: 0, fontSize: 18 }}>{t('settings_title').toUpperCase()}</h2>
              </div>
              <button onClick={() => closeSettings()} style={{ background: 'none', border: 'none', color: 'var(--on-surface-variant)', fontSize: '1.5rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>

            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: 0 }}>

              {/* ── ADMIN-ONLY: Logo, Teamname, Farbe ── */}
              {isAdmin ? (
                <>
                  {/* Logo */}
                  <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 12 }}>{t('nav_team_logo')}</div>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                      <div style={{ width: 56, height: 56, background: 'var(--surface-high)', border: '1px solid rgba(59,73,75,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', overflow: 'hidden', flexShrink: 0 }}>
                        {logoBase64 ? <img src={logoBase64} alt="Logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <BrandMark size={36} variant="cyan" />}
                      </div>
                      <div style={{ flex: 1 }}>
                        <input type="file" accept="image/*" onChange={handleLogoUpload} style={{ fontSize: 11, color: 'var(--on-surface-variant)', marginBottom: 8, display: 'block', width: '100%' }} />
                        <button className="btn btn-secondary" style={{ fontSize: 10, padding: '4px 10px' }} onClick={() => setLogoBase64('')}>{t('nav_remove')}</button>
                      </div>
                    </div>
                  </div>

                  {/* Team Name */}
                  <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 12 }}>{t('nav_team_name')}</div>
                    <input
                      type="text"
                      value={teamNameInput}
                      onChange={(e) => setTeamNameInput(e.target.value.toUpperCase())}
                      maxLength={32}
                      placeholder="TEAMNAME"
                      style={{ background: 'transparent', border: 'none', borderBottom: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 800, fontStyle: 'italic', letterSpacing: '0.06em', padding: '6px 0', outline: 'none', width: '100%' }}
                      onFocus={(e) => e.target.style.borderBottomColor = 'var(--primary)'}
                      onBlur={(e) => e.target.style.borderBottomColor = 'var(--outline-variant)'}
                    />
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--outline)', marginTop: 6 }}>
                      Wird in der Sidebar und im Login angezeigt.
                    </div>
                  </div>

                  {/* Color */}
                  <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 12 }}>{t('nav_primary_color')}</div>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                      <input type="color" value={colorPrimary} onChange={(e) => applyColor(e.target.value)} style={{ width: 40, height: 32, cursor: 'pointer', border: 'none', background: 'none' }} />
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: 'var(--on-surface-variant)' }}>{colorPrimary}</span>
                      <button className="btn btn-secondary" style={{ fontSize: 10, padding: '4px 10px' }} onClick={() => applyColor('var(--brand-primary)')}>{t('nav_reset')}</button>
                    </div>
                    <div style={{ padding: '0.5rem 1rem', background: 'var(--primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textAlign: 'center' }}>
                      VORSCHAU
                    </div>
                  </div>
                </>
              ) : (
                /* Fahrer: schreibgeschützte Anzeige */
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(171,214,0,0.06)', border: '1px solid rgba(171,214,0,0.2)' }}>
                    <div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--brand-secondary)', marginBottom: 2 }}>{t('nav_admin_only')}</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'var(--on-surface-variant)' }}>
                        Teamname und Farben können nur vom Owner oder Team Manager geändert werden.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Password Change */}
              <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 12 }}>{t('nav_pw_change')}</div>
                <form onSubmit={handlePasswordChange} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[
                    { label: t('nav_pw_current'), value: pwCurrent, setter: setPwCurrent },
                    { label: t('nav_pw_new'),     value: pwNew,     setter: setPwNew },
                    { label: t('nav_pw_confirm'), value: pwConfirm, setter: setPwConfirm },
                  ].map(({ label, value, setter }) => (
                    <div key={label} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <label style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', color: 'var(--outline)', textTransform: 'uppercase' }}>{label}</label>
                      <input
                        type="password"
                        value={value}
                        onChange={(e) => setter(e.target.value)}
                        required
                        style={{ background: 'transparent', border: 'none', borderBottom: '1px solid var(--outline-variant)', color: 'var(--on-surface)', fontFamily: "var(--font-display)", fontSize: 14, padding: '6px 0', outline: 'none', width: '100%' }}
                        onFocus={(e) => e.target.style.borderBottomColor = 'var(--primary)'}
                        onBlur={(e) => e.target.style.borderBottomColor = 'var(--outline-variant)'}
                      />
                    </div>
                  ))}
                  {pwMessage && (
                    <div style={{ padding: '8px 12px', background: pwMessage.type === 'success' ? 'rgba(var(--status-success-rgb,187,255,140),0.1)' : 'rgba(255,180,171,0.1)', border: `1px solid ${pwMessage.type === 'success' ? 'var(--status-success)' : 'rgba(255,180,171,0.3)'}`, fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: pwMessage.type === 'success' ? 'var(--status-success)' : 'var(--error)', letterSpacing: '0.05em' }}>
                      {pwMessage.text}
                    </div>
                  )}
                  <button type="submit" disabled={pwLoading} className="btn btn-primary" style={{ alignSelf: 'flex-start', opacity: pwLoading ? 0.6 : 1 }}>
                    {pwLoading ? '...' : t('nav_pw_change').toUpperCase()}
                  </button>
                </form>
              </div>

              {/* Push Notifications */}
              {'Notification' in window && (
                <div style={{ marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(59,73,75,0.2)' }}>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 12 }}>{t('settings_section_push')}</div>

                  {/* Status badge */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: pushPermission === 'granted' && pushEnabled ? 'var(--brand-secondary)'
                          : pushPermission === 'denied' ? '#ffb4ab'
                          : 'rgba(132,148,149,0.5)',
                        boxShadow: pushPermission === 'granted' && pushEnabled ? '0 0 6px var(--brand-secondary)' : 'none',
                      }} />
                      <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: 'var(--on-surface-variant)', letterSpacing: '0.08em' }}>
                        {pushPermission === 'denied' ? 'BLOCKIERT'
                          : pushPermission === 'granted' && pushEnabled ? 'AKTIV'
                          : 'INAKTIV'}
                      </span>
                    </div>

                    {/* Toggle — only show if not denied */}
                    {pushPermission !== 'denied' && (
                      <button
                        onClick={handlePushToggle}
                        disabled={pushLoading}
                        style={{
                          position: 'relative',
                          width: 44, height: 24,
                          borderRadius: 12,
                          background: pushEnabled ? 'var(--primary)' : 'rgba(59,73,75,0.4)',
                          border: 'none', cursor: pushLoading ? 'not-allowed' : 'pointer',
                          transition: 'background 0.2s', flexShrink: 0, outline: 'none',
                          opacity: pushLoading ? 0.6 : 1,
                        }}
                        aria-label="Push-Benachrichtigungen umschalten"
                      >
                        <span style={{
                          position: 'absolute',
                          top: 3, left: pushEnabled ? 23 : 3,
                          width: 18, height: 18,
                          borderRadius: '50%',
                          background: '#fff',
                          transition: 'left 0.2s',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                        }} />
                      </button>
                    )}
                  </div>

                  {/* Feedback messages */}
                  {pushMsg && pushMsg.type !== 'blocked' && (
                    <div style={{ padding: '8px 12px', background: pushMsg.type === 'success' ? 'rgba(var(--status-success-rgb,187,255,140),0.1)' : pushMsg.type === 'info' ? 'rgba(59,73,75,0.2)' : 'rgba(255,180,171,0.1)', border: `1px solid ${pushMsg.type === 'success' ? 'var(--status-success)' : pushMsg.type === 'info' ? 'rgba(59,73,75,0.4)' : 'rgba(255,180,171,0.3)'}`, fontFamily: "var(--font-display)", fontSize: 12, color: pushMsg.type === 'success' ? 'var(--status-success)' : pushMsg.type === 'info' ? 'var(--on-surface-variant)' : 'var(--error)', lineHeight: 1.4, marginBottom: 4 }}>
                      {pushMsg.text}
                    </div>
                  )}

                  {/* Blocked guide */}
                  {(pushPermission === 'denied' || (pushMsg && pushMsg.type === 'blocked')) && (
                    <div style={{ padding: '12px 14px', background: 'rgba(255,180,171,0.07)', border: '1px solid rgba(255,180,171,0.2)' }}>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, color: '#ffb4ab', letterSpacing: '0.1em', marginBottom: 8 }}>BERECHTIGUNG BLOCKIERT</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'var(--on-surface-variant)', lineHeight: 1.6 }}>
                        Du hast Benachrichtigungen blockiert. So entsperrst du sie:
                        <br /><br />
                        <strong style={{ color: 'var(--on-surface)' }}>Safari (iOS):</strong> Einstellungen → Safari → Erweitert → Website-Daten oder Einstellungen → Benachrichtigungen
                        <br /><br />
                        <strong style={{ color: 'var(--on-surface)' }}>Chrome (Android):</strong> Adressleiste → Schloss-Symbol → Website-Einstellungen → Benachrichtigungen → Zulassen
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* PWA Install */}
              <div style={{ marginBottom: '1.5rem' }}>
                <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--outline)', textTransform: 'uppercase', marginBottom: 12 }}>App Installation</div>
                {installDone ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: 'rgba(var(--brand-secondary-rgb),0.08)', border: '1px solid rgba(var(--brand-secondary-rgb),0.25)' }}>
                    <span style={{ fontSize: 16 }}>✓</span>
                    <div>
                      <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, color: 'var(--brand-secondary)', letterSpacing: '0.1em' }}>BEREITS INSTALLIERT</div>
                      <div style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'var(--on-surface-variant)', marginTop: 2 }}>App läuft als Standalone-PWA</div>
                    </div>
                  </div>
                ) : installable ? (
                  <div>
                    <p style={{ fontFamily: "var(--font-display)", fontSize: 13, color: 'var(--on-surface-variant)', marginBottom: 12, lineHeight: 1.5 }}>
                      App zum Home-Bildschirm hinzufügen für schnellen Zugriff ohne Browser.
                    </p>
                    <button
                      onClick={handleInstall}
                      style={{ width: '100%', padding: '12px 16px', background: 'var(--primary)', color: '#002022', fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 0 16px rgba(var(--brand-primary-rgb),0.3)' }}
                    >
                      <span style={{ fontSize: 16 }}>⬇</span>
                      AUF HOME-BILDSCHIRM SPEICHERN
                    </button>
                  </div>
                ) : (
                  <div style={{ padding: '12px 16px', background: 'rgba(26,28,32,0.6)', border: '1px solid rgba(59,73,75,0.3)' }}>
                    <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, fontWeight: 700, color: 'var(--outline)', letterSpacing: '0.1em', marginBottom: 6 }}>MANUELL INSTALLIEREN</div>
                    <p style={{ fontFamily: "var(--font-display)", fontSize: 12, color: 'var(--on-surface-variant)', lineHeight: 1.6, margin: 0 }}>
                      <strong style={{ color: 'var(--on-surface)' }}>iOS Safari:</strong> Teilen-Symbol → „Zum Home-Bildschirm"<br />
                      <strong style={{ color: 'var(--on-surface)' }}>Android Chrome:</strong> Menü (⋮) → „Zum Startbildschirm"
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div style={{ padding: '1rem 1.5rem', borderTop: '1px solid rgba(59,73,75,0.2)' }}>
              {saveError && (
                <div style={{ marginBottom: 10, padding: '8px 12px', background: 'rgba(255,180,171,0.1)', border: '1px solid rgba(255,180,171,0.3)', fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: 'var(--error)', letterSpacing: '0.05em' }}>
                  {saveError}
                </div>
              )}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button className="btn btn-secondary" onClick={() => closeSettings()}>Schließen</button>
                {isAdmin && <button className="btn btn-primary" onClick={saveSettings}>Speichern</button>}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Navigation;
